import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkIcon from '@mui/icons-material/Work';
import useScrollToTop from '../../hooks/useScrollToTop';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chile-tabpanel-${index}`}
      aria-labelledby={`chile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const Chile: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [selectedTab, setSelectedTab] = useState(0);
  useScrollToTop();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const businessBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.chile.business.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.chile.business.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.chile.business.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.chile.business.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.chile.business.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.chile.business.benefits.quality') },
  ];

  const birthBenefits = [
    { icon: <FlightIcon />, text: t('citizenship.chile.birth.benefits.travel') },
    { icon: <WorkIcon />, text: t('citizenship.chile.birth.benefits.work') },
    { icon: <SchoolIcon />, text: t('citizenship.chile.birth.benefits.education') },
    { icon: <BusinessIcon />, text: t('citizenship.chile.birth.benefits.business') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.chile.birth.benefits.family') },
    { icon: <CheckCircleIcon />, text: t('citizenship.chile.birth.benefits.quality') },
  ];

  const businessRequirements = [
    t('citizenship.chile.business.requirements.investment'),
    t('citizenship.chile.business.requirements.criminal'),
    t('citizenship.chile.business.requirements.passport'),
    t('citizenship.chile.business.requirements.financial'),
    t('citizenship.chile.business.requirements.fee'),
    t('citizenship.chile.business.requirements.insurance'),
  ];

  const birthRequirements = [
    t('citizenship.chile.birth.requirements.visa'),
    t('citizenship.chile.birth.requirements.medical'),
    t('citizenship.chile.birth.requirements.fees'),
    t('citizenship.chile.birth.requirements.compliance'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#d32f2f',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.chile.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.chile.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Tabs */}
      <Container sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 'medium',
                minWidth: 200,
              },
            }}
          >
            <Tab label={t('citizenship.chile.tabs.business')} />
            <Tab label={t('citizenship.chile.tabs.birth')} />
          </Tabs>
        </Box>
      </Container>

      {/* Business Program */}
      <TabPanel value={selectedTab} index={0}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.chile.business.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.chile.business.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.chile.business.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Business Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.chile.business.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {businessBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Business Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.chile.business.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.chile.business.requirements.investmentTitle')}
                    </Typography>
                    <List>
                      {businessRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.chile.business.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.business.timeline.initial.title')}
                          secondary={t('citizenship.chile.business.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.business.timeline.residence.title')}
                          secondary={t('citizenship.chile.business.timeline.residence.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.business.timeline.permanent.title')}
                          secondary={t('citizenship.chile.business.timeline.permanent.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.business.timeline.citizenship.title')}
                          secondary={t('citizenship.chile.business.timeline.citizenship.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>

      {/* Birth Program */}
      <TabPanel value={selectedTab} index={1}>
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.chile.birth.overview.title')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.chile.birth.overview.description1')}
                </Typography>
                <Typography variant="body1" paragraph align="center">
                  {t('citizenship.chile.birth.overview.description2')}
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Birth Benefits */}
        <Container sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" gutterBottom align="center">
                  {t('citizenship.chile.birth.benefits.title')}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center">
                  {birthBenefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Box sx={{ color: 'primary.main', mr: 2 }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" component="h3">
                                {benefit.text}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Birth Requirements */}
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('citizenship.chile.birth.requirements.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('citizenship.chile.birth.requirements.title')}
                    </Typography>
                    <List>
                      {birthRequirements.map((requirement, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={requirement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('citizenship.chile.birth.timeline.title')}
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.birth.timeline.initial.title')}
                          secondary={t('citizenship.chile.birth.timeline.initial.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.birth.timeline.registration.title')}
                          secondary={t('citizenship.chile.birth.timeline.registration.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.birth.timeline.residency.title')}
                          secondary={t('citizenship.chile.birth.timeline.residency.description')}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={t('citizenship.chile.birth.timeline.transition.title')}
                          secondary={t('citizenship.chile.birth.timeline.transition.description')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>
    </Box>
  );
};

export default Chile;
